import React from 'react';
import { Link, Typography, styled } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import CodeSnippet from './CodeSnippet/CodeSnippet';
import PageTitleSection from '../../components/PageTitleSection/PageTitleSection';
import PageContentContainer from '../../components/PageContentContainer/PageContentContainer';

const SubHeading = styled('h2')(({ theme }) => ({
  ...theme.typography.h2,
  fontSize: '1rem',
  fontWeight: 'bold',
  marginBottom: theme.spacing(2)
}));

const OptionHeading = styled('h3')(({ theme }) => ({
  ...theme.typography.h3,
  fontSize: '1rem',
  fontWeight: '500',
  marginBottom: theme.spacing(2)
}));

const CustomTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  color: '#374151'
}));

function EmbeddingInstructionsPage(): React.ReactElement {
  const { currentChatbot } = React.useContext(ChatbotContext);

  const bodyCode = '</body>';
  const embedScriptDefault = `<script src="https://chatbot.japeto.ai/loader.js" data-botid="${currentChatbot?.uuid}"></script>`;
  const embedScriptFullSize = `<iframe src="https://app.japeto.ai/public/chatbots/${currentChatbot?.uuid}" frameborder="0" height="1000px" width="700px" style="border: none;"></iframe>`;

  return (
    <>
      <Helmet>
        <title>Japeto - Embedding Instructions</title>
      </Helmet>
      <PageTitleSection title='Embedding' />
      <PageContentContainer>
        <div className='max-w-2xl mx-auto'>
          <SubHeading>Step 1: Access Your Website&apos;s HTML</SubHeading>
          <CustomTypography variant='body2'>
            To embed the chatbot widget, you need to edit the HTML code of your
            website. If you&apos;re using a website builder (like WordPress,
            Wix, or Squarespace), log in to your website&apos;s dashboard. If
            you&apos;re editing raw HTML files, open the file for the page where
            you want the chatbot to appear in a text editor.
          </CustomTypography>

          <SubHeading>Step 2: Insert the Chatbot Code</SubHeading>
          <CustomTypography variant='body2' sx={{ marginBottom: 2 }}>
            We provide two options here depending on your use cases:
          </CustomTypography>

          <OptionHeading>- Option 1 (recommended)</OptionHeading>
          <CustomTypography variant='body2' sx={{ marginBottom: 1 }}>
            To embed the chatbot widget in a fixed position with our default
            sizing, scroll to the bottom of the HTML code of your webpage and
            look for a line that looks like this:{' '}
            <code style={{ fontWeight: 'bold' }}>{bodyCode}</code> . This is
            near the end of your page&apos;s code.
          </CustomTypography>
          <CustomTypography variant='body2' sx={{ marginBottom: 1 }}>
            Just before the{' '}
            <code style={{ fontWeight: 'bold' }}>{bodyCode}</code> tag, simply
            paste the following code snippet:
          </CustomTypography>
          <CodeSnippet embedScript={embedScriptDefault} />

          <OptionHeading>- Option 2 (custom sizing)</OptionHeading>
          <CustomTypography variant='body2' sx={{ marginBottom: 1 }}>
            To embed the chatbot widget in a container with custom sizing and
            position anywhere on your page, simply paste the following code
            snippet in the area of the page where you&apos;d like the chatbot
            adjusting the height and width as needed:
          </CustomTypography>
          <CodeSnippet embedScript={embedScriptFullSize} />

          <SubHeading sx={{ mt: 4 }}>Step 3: Save Your Changes</SubHeading>
          <CustomTypography variant='body2'>
            After pasting the code, save your changes. If you&apos;re using a
            website builder, use the save or publish button provided. If
            you&apos;re editing an HTML file, save the file and upload it to
            your server.
          </CustomTypography>

          <SubHeading>Step 4: Check Your Website</SubHeading>
          <CustomTypography variant='body2'>
            Visit your website in a web browser to see the chatbot in action. It
            should now appear on the page where you added the code.
          </CustomTypography>

          <SubHeading>Step 5: Contact Support if Needed</SubHeading>
          <CustomTypography variant='body2'>
            If you encounter any issues or the chatbot doesn&apos;t appear,
            reach out to our support team for assistance on{' '}
            <Link href='mailto:support@japeto.ai'>support@japeto.ai</Link>. Make
            sure to describe the steps you&apos;ve taken and any error messages
            you might have seen.
          </CustomTypography>
        </div>
      </PageContentContainer>
    </>
  );
}

export default EmbeddingInstructionsPage;
